import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { EmptyState } from '../components/ui/Cards';
import SafeIcon from '../common/SafeIcon';
import SolDivider from '../components/ui/SolDivider';
import { motion, AnimatePresence } from 'framer-motion';

export default function Academia() {
  const {
    trainings, reels, members, currentUser,
    submitTraining, submitReel, flagContent, CATEGORIAS_ACADEMIA,
  } = useApp();
  
  // Main Tab State: Capacitaciones (Long form) vs Reels (Short form)
  const [mainTab, setMainTab] = useState('capacitaciones');
  
  // Sub Tab State for Capacitaciones
  const [activeCategory, setActiveCategory] = useState('Todos');

  const [showSubmit, setShowSubmit] = useState(false);
  const [submitType, setSubmitType] = useState('training'); // training | reel
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [trainingForm, setTrainingForm] = useState({
    titulo: '', descripcion: '', categoria: CATEGORIAS_ACADEMIA?.[0] || 'Networking',
    duracion: '', embed_url: '',
  });
  const [reelForm, setReelForm] = useState({
    titulo: '', thumbnail_url: '', linked_training_id: '',
  });
  const [flagTarget, setFlagTarget] = useState(null); // { type, id, titulo }
  const [flagMotivo, setFlagMotivo] = useState('');
  const [flagError, setFlagError] = useState('');

  const canSubmit = ['colaborador', 'moderador', 'admin'].includes(currentUser?.role);
  const canFlagContent = ['moderador', 'admin'].includes(currentUser?.role);
  
  const approvedTrainings = trainings.filter(t => t.status === 'approved');
  const approvedReels = reels.filter(r => r.status === 'approved');
  const categories = ['Todos', ...new Set([
    ...(CATEGORIAS_ACADEMIA || []),
    ...approvedTrainings.map(t => t.categoria).filter(Boolean),
  ])];
  
  const filteredTrainings = activeCategory === 'Todos' 
    ? approvedTrainings 
    : approvedTrainings.filter(t => t.categoria === activeCategory);

  const openSubmit = () => {
    if (!canSubmit) return;
    setSubmitError('');
    setSubmitType(mainTab === 'reels' ? 'reel' : 'training');
    setShowSubmit(true);
  };

  const handleSubmitContent = async (e) => {
    e.preventDefault();
    setSubmitError('');
    try {
      if (submitType === 'training') {
        await submitTraining(trainingForm);
        setTrainingForm({
          titulo: '', descripcion: '', categoria: CATEGORIAS_ACADEMIA?.[0] || 'Networking',
          duracion: '', embed_url: '',
        });
      } else {
        await submitReel({
          ...reelForm,
          linked_training_id: reelForm.linked_training_id || null,
        });
        setReelForm({ titulo: '', thumbnail_url: '', linked_training_id: '' });
      }
      setShowSubmit(false);
      setSubmitSuccess(true);
      setTimeout(() => setSubmitSuccess(false), 4000);
    } catch (err) {
      console.error('submit content failed:', err);
      setSubmitError(err.message || 'No se pudo enviar el contenido');
    }
  };

  const openFlag = (type, item) => {
    setFlagError('');
    setFlagMotivo('');
    setFlagTarget({ type, id: item.id, titulo: item.titulo });
  };

  const handleFlagContent = async (e) => {
    e.preventDefault();
    if (!flagTarget) return;
    setFlagError('');
    try {
      await flagContent(flagTarget.type, flagTarget.id, flagMotivo);
      setFlagTarget(null);
      setFlagMotivo('');
    } catch (err) {
      console.error('flagContent failed:', err);
      setFlagError(err.message || 'No se pudo reportar el contenido');
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-10">
      
      {/* Hero Section */}
      <div className="relative">
        <div className="absolute -inset-x-10 -top-10 h-[250px] bg-[#74ACDF]/5 -z-10 rounded-b-[100px]" />
        
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <span className="label-technical text-[#F6B40E] mb-2 block">Academia del Movimiento</span>
            <h1 className="text-4xl md:text-6xl font-editorial font-bold text-[#1B2A4E] leading-tight">
              Aprender de la <span className="text-[#74ACDF] italic">comunidad.</span>
            </h1>
            <p className="text-lg text-stone-500 mt-4 max-w-lg">
              Capacitaciones prácticas y clips cortos hechos por y para emprendedores argentinos en Estados Unidos.
            </p>
          </div>
          {canSubmit && (
            <button
              onClick={openSubmit}
              className="bg-[#1B2A4E] hover:bg-[#253969] text-white px-6 py-3 rounded-xl font-bold shadow-lg transition-transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
            >
              <SafeIcon name="Plus" className="w-5 h-5" /> Aportar Contenido
            </button>
          )}
        </div>

        {/* Main Tabs (Capacitaciones vs Reels) */}
        <div className="mt-10 flex bg-white p-1.5 rounded-2xl w-fit shadow-lg shadow-stone-200/50 border border-stone-100 overflow-x-auto no-scrollbar">
          <button 
            onClick={() => setMainTab('capacitaciones')} 
            className={`px-6 md:px-8 py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap flex items-center gap-2 ${mainTab === 'capacitaciones' ? 'bg-[#1B2A4E] text-[#F6B40E] shadow-md' : 'text-stone-500 hover:text-[#1B2A4E]'}`}
          >
            <SafeIcon name="Video" className="w-4 h-4" /> Capacitaciones
          </button>
          
          <div className="w-px bg-stone-200 my-2 mx-2"></div>
          
          <button 
            onClick={() => setMainTab('reels')} 
            className={`px-6 md:px-8 py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap flex items-center gap-2 ${mainTab === 'reels' ? 'bg-[#1B2A4E] text-[#F6B40E] shadow-md' : 'text-stone-500 hover:text-[#1B2A4E]'}`}
          >
            <SafeIcon name="Film" className="w-4 h-4" /> Reels (Clips Cortos)
          </button>
        </div>
      </div>

      <SolDivider />

      {/* View: Capacitaciones */}
      {mainTab === 'capacitaciones' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
          {/* Sub Categories Tabs */}
          <div className="flex overflow-x-auto no-scrollbar gap-2 pb-2">
            {categories.map(cat => (
              <button 
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`whitespace-nowrap px-6 py-3 rounded-full text-sm font-bold transition-all ${activeCategory === cat ? 'bg-[#74ACDF] text-white shadow-md shadow-[#74ACDF]/20' : 'bg-white text-stone-500 border border-stone-200 hover:border-[#74ACDF] hover:text-[#1B2A4E]'}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {filteredTrainings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {filteredTrainings.map((t, idx) => {
                const presenter = members.find(m => m.id === t.presentado_por_member_id);
                return (
                  <motion.div 
                    key={t.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-white rounded-[24px] border border-stone-100 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group cursor-pointer relative"
                  >
                    <div className="absolute top-0 left-0 w-full h-1 bg-[#F6B40E] opacity-0 group-hover:opacity-100 transition-opacity z-10" />
                    <div className="h-56 relative overflow-hidden">
                      <img src={t.embed_url} alt={t.titulo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1B2A4E]/80 via-transparent to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-16 h-16 bg-[#F6B40E] rounded-full flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-transform">
                          <SafeIcon name="Play" className="w-6 h-6 text-[#1B2A4E] ml-1" />
                        </div>
                      </div>
                      <div className="absolute bottom-4 right-4 bg-white/20 backdrop-blur-md text-white text-xs px-3 py-1.5 rounded-full font-bold border border-white/30">
                        {t.duracion}
                      </div>
                    </div>
                    <div className="p-6 md:p-8">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="label-technical text-[#74ACDF]">{t.categoria}</div>
                        {canFlagContent && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openFlag('training', t);
                            }}
                            className="shrink-0 text-xs font-bold text-red-500 hover:text-red-600 flex items-center gap-1"
                            title="Reportar contenido"
                          >
                            <SafeIcon name="Flag" className="w-3.5 h-3.5" /> Reportar
                          </button>
                        )}
                      </div>
                      <h3 className="font-editorial font-bold text-2xl text-[#1B2A4E] mb-3 leading-tight group-hover:text-[#74ACDF] transition-colors">{t.titulo}</h3>
                      <p className="text-stone-500 text-sm line-clamp-2 mb-6 leading-relaxed">{t.descripcion}</p>
                      
                      {(presenter || t.presentado_por_externo) && (
                        <div className="flex items-center justify-between pt-6 border-t border-stone-100">
                          <div className="flex items-center gap-3">
                            {presenter ? (
                              <img src={presenter.foto_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(presenter.nombre || '?')}`} alt="" className="w-10 h-10 rounded-full object-cover ring-2 ring-[#74ACDF]/10" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center">
                                <SafeIcon name="User" className="w-5 h-5 text-stone-400" />
                              </div>
                            )}
                            <div>
                              <span className="block text-sm font-bold text-[#1B2A4E]">{presenter?.nombre || t.presentado_por_externo}</span>
                              <span className="block text-[10px] uppercase tracking-widest text-stone-400 font-bold">Presentador</span>
                            </div>
                          </div>
                          <SafeIcon name="ArrowRight" className="w-5 h-5 text-[#74ACDF] group-hover:translate-x-1 transition-transform" />
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <EmptyState icon="Video" title="No hay capacitaciones aún" message="Actualmente no hay contenido en esta categoría. ¡Sé el primero en aportar!" />
          )}
        </motion.div>
      )}

      {/* View: Reels */}
      {mainTab === 'reels' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
          {approvedReels.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {approvedReels.map((reel, idx) => {
                const linkedTraining = trainings.find(t => t.id === (reel.linked_training_id || reel.training_id));
                const creator = members.find(m => m.id === (reel.member_id || reel.submitted_by));
                return (
                  <motion.div 
                    key={reel.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="group relative aspect-[9/16] bg-stone-900 rounded-[32px] overflow-hidden shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                  >
                    <div className="absolute top-0 left-0 w-full h-1 bg-[#F6B40E] opacity-0 group-hover:opacity-100 transition-opacity z-20" />
                    <img src={reel.thumbnail_url || reel.embed_url} alt={reel.titulo} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1B2A4E] via-[#1B2A4E]/40 to-transparent opacity-90"></div>
                    
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                      <div className="w-16 h-16 bg-[#F6B40E] rounded-full flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-transform">
                        <SafeIcon name="Play" className="w-6 h-6 text-[#1B2A4E] ml-1" />
                      </div>
                    </div>
                    
                    <div className="absolute inset-x-0 bottom-0 p-5 z-20 flex flex-col justify-end">
                      {canFlagContent && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openFlag('reel', reel);
                          }}
                          className="mb-3 self-start bg-white/15 backdrop-blur-md text-white text-[10px] px-3 py-1.5 rounded-full font-bold border border-white/25 hover:bg-red-500/80 transition-colors flex items-center gap-1.5"
                        >
                          <SafeIcon name="Flag" className="w-3 h-3" /> Reportar
                        </button>
                      )}
                      {linkedTraining && (
                        <div className="bg-[#74ACDF]/20 backdrop-blur-md text-[#74ACDF] text-[9px] px-3 py-1.5 rounded-full font-bold mb-3 border border-[#74ACDF]/30 w-fit label-technical flex items-center gap-1.5">
                          <SafeIcon name="Link" className="w-3 h-3" /> Extraído de capacitación
                        </div>
                      )}
                      <h3 className="font-editorial font-bold text-lg md:text-xl text-white mb-2 leading-tight group-hover:text-[#74ACDF] transition-colors line-clamp-3">
                        {reel.titulo}
                      </h3>
                      {creator && (
                        <div className="flex items-center gap-2 mt-2 opacity-80 group-hover:opacity-100 transition-opacity">
                          <img src={creator.foto_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(creator.nombre || '?')}`} alt="" className="w-6 h-6 rounded-full object-cover ring-1 ring-white/20" />
                          <span className="text-xs text-white/90 font-medium">{creator.nombre}</span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <EmptyState icon="Film" title="No hay reels todavía" message="Sé el primero en compartir un clip corto con un aprendizaje valioso." />
          )}
        </motion.div>
      )}

      {/* Bottom Navy Band CTA */}
      <div className="mt-20 p-12 bg-[#1B2A4E] rounded-[40px] text-white text-center relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#74ACDF]/10 rounded-full -mr-32 -mt-32 blur-2xl" />
        <div className="relative z-10">
          <h2 className="text-3xl md:text-4xl font-editorial font-bold mb-4">¿Tenés conocimiento para compartir?</h2>
          <p className="text-[#74ACDF] mb-8 max-w-md mx-auto font-medium text-lg">Proponé una capacitación o un clip corto y ayudá a otros miembros de la comunidad a crecer.</p>
          <button
            onClick={openSubmit}
            disabled={!canSubmit}
            className="bg-[#F6B40E] hover:bg-[#e5a70d] text-[#1B2A4E] font-bold py-4 px-10 rounded-2xl transition-all hover:scale-105 shadow-xl disabled:opacity-50 disabled:hover:scale-100"
          >
            {canSubmit ? 'Aportar a la Academia' : 'Disponible para colaboradores'}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {submitSuccess && (
          <motion.div
            initial={{ opacity: 0, y: 50, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: 50, x: '-50%' }}
            className="fixed bottom-24 left-1/2 z-50 bg-green-500 text-white px-6 py-4 rounded-2xl shadow-2xl font-medium flex items-center gap-3"
          >
            <SafeIcon name="Check" className="w-5 h-5" />
            Contenido enviado. Un moderador lo revisará.
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSubmit && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowSubmit(false)} className="absolute inset-0 bg-[#1B2A4E]/60 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[32px] shadow-2xl w-full max-w-xl relative z-10 overflow-hidden border border-stone-100 max-h-[90vh] overflow-y-auto"
            >
              <div className="p-8 border-b border-stone-100 bg-[#FAF9F6]">
                <h2 className="text-2xl font-editorial font-bold text-[#1B2A4E]">Aportar a la Academia</h2>
                <p className="text-sm text-stone-500 mt-1">El contenido queda pendiente de aprobación.</p>
              </div>
              <form onSubmit={handleSubmitContent} className="p-8 space-y-5">
                {submitError && (
                  <div className="p-3 rounded-xl bg-red-50 text-red-600 text-sm font-medium">{submitError}</div>
                )}
                <div className="flex gap-2 bg-stone-50 p-1.5 rounded-xl border border-stone-200">
                  <button type="button" onClick={() => setSubmitType('training')} className={`flex-1 py-2 rounded-lg text-sm font-bold ${submitType === 'training' ? 'bg-white shadow text-[#1B2A4E]' : 'text-stone-500'}`}>Capacitación</button>
                  <button type="button" onClick={() => setSubmitType('reel')} className={`flex-1 py-2 rounded-lg text-sm font-bold ${submitType === 'reel' ? 'bg-white shadow text-[#1B2A4E]' : 'text-stone-500'}`}>Reel</button>
                </div>

                {submitType === 'training' ? (
                  <>
                    <div>
                      <label className="block label-technical text-stone-500 mb-2">Título</label>
                      <input required className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 outline-none focus:ring-2 focus:ring-[#74ACDF]/50" value={trainingForm.titulo} onChange={(e) => setTrainingForm({ ...trainingForm, titulo: e.target.value })} />
                    </div>
                    <div>
                      <label className="block label-technical text-stone-500 mb-2">Descripción</label>
                      <textarea required rows={3} className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 outline-none focus:ring-2 focus:ring-[#74ACDF]/50" value={trainingForm.descripcion} onChange={(e) => setTrainingForm({ ...trainingForm, descripcion: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block label-technical text-stone-500 mb-2">Categoría</label>
                        <select className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 outline-none" value={trainingForm.categoria} onChange={(e) => setTrainingForm({ ...trainingForm, categoria: e.target.value })}>
                          {(CATEGORIAS_ACADEMIA || []).map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block label-technical text-stone-500 mb-2">Duración</label>
                        <input required className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 outline-none" placeholder="30 min" value={trainingForm.duracion} onChange={(e) => setTrainingForm({ ...trainingForm, duracion: e.target.value })} />
                      </div>
                    </div>
                    <div>
                      <label className="block label-technical text-stone-500 mb-2">URL de portada / embed</label>
                      <input required type="url" className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 outline-none text-sm" value={trainingForm.embed_url} onChange={(e) => setTrainingForm({ ...trainingForm, embed_url: e.target.value })} />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block label-technical text-stone-500 mb-2">Título</label>
                      <input required className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 outline-none focus:ring-2 focus:ring-[#74ACDF]/50" value={reelForm.titulo} onChange={(e) => setReelForm({ ...reelForm, titulo: e.target.value })} />
                    </div>
                    <div>
                      <label className="block label-technical text-stone-500 mb-2">URL de thumbnail</label>
                      <input required type="url" className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 outline-none text-sm" value={reelForm.thumbnail_url} onChange={(e) => setReelForm({ ...reelForm, thumbnail_url: e.target.value })} />
                    </div>
                    <div>
                      <label className="block label-technical text-stone-500 mb-2">Capacitación vinculada (opcional)</label>
                      <select className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 outline-none" value={reelForm.linked_training_id} onChange={(e) => setReelForm({ ...reelForm, linked_training_id: e.target.value })}>
                        <option value="">Ninguna</option>
                        {approvedTrainings.map((t) => <option key={t.id} value={t.id}>{t.titulo}</option>)}
                      </select>
                    </div>
                  </>
                )}

                <div className="pt-4 flex justify-end gap-3">
                  <button type="button" onClick={() => setShowSubmit(false)} className="px-6 py-3 rounded-xl font-bold text-stone-500 hover:bg-stone-100">Cancelar</button>
                  <button type="submit" className="px-8 py-3 rounded-xl font-bold bg-[#1B2A4E] text-white hover:bg-[#253969] shadow-md">Enviar</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {flagTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setFlagTarget(null)} className="absolute inset-0 bg-[#1B2A4E]/60 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[32px] shadow-2xl w-full max-w-md relative z-10 overflow-hidden border border-stone-100"
            >
              <div className="p-8 border-b border-stone-100 bg-[#FAF9F6]">
                <h2 className="text-2xl font-editorial font-bold text-[#1B2A4E]">Reportar contenido</h2>
                <p className="text-sm text-stone-500 mt-1 line-clamp-2">"{flagTarget.titulo}"</p>
              </div>
              <form onSubmit={handleFlagContent} className="p-8 space-y-5">
                {flagError && (
                  <div className="p-3 rounded-xl bg-red-50 text-red-600 text-sm font-medium">{flagError}</div>
                )}
                <div>
                  <label className="block label-technical text-stone-500 mb-2">Motivo</label>
                  <textarea
                    required
                    rows={3}
                    className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 outline-none focus:ring-2 focus:ring-[#74ACDF]/50"
                    placeholder="¿Por qué reportás este contenido?"
                    value={flagMotivo}
                    onChange={(e) => setFlagMotivo(e.target.value)}
                  />
                </div>
                <div className="pt-2 flex justify-end gap-3">
                  <button type="button" onClick={() => setFlagTarget(null)} className="px-6 py-3 rounded-xl font-bold text-stone-500 hover:bg-stone-100">Cancelar</button>
                  <button type="submit" className="px-8 py-3 rounded-xl font-bold bg-red-500 text-white hover:bg-red-600 shadow-md">Reportar</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </motion.div>
  );
}
